import React, { useState, useCallback, useMemo, useEffect } from 'react';

import { getEORIValidation, getDescriptionValidation } from '../../../api/util';
import { getEntryPortCountryCode } from '../../../utils/countries';
import { MANDATORY_EORI_COUNTRIES } from '../utils';

const useErrors = (
  state,
  referenceData,
  toRunInitialErrorCheck,
  setToRunInitialErrorCheck,
  setGoToNextStepFunc,
  setActiveStep,
  errorsChecked,
  setErrorsChecked,
  consignmentsVisited,
  currentConsignmentErrors,
  setCurrentConsignmentErrors,
  currentConsignmentHMRCErrors,
  setCurrentConsignmentHMRCErrors,
  currentConsignment,
  data,
  isEditable
) => {
  const [errors, setErrors] = useState({});
  const [errorsHMRC, setErrorsHMRC] = useState({});

  const [errorsLocal, setErrorsLocal] = useState({});

  const [initialErrorCheckRun, setInitialErrorCheckRun] = useState(false);

  const [isAccompaniedLoaded, setIsAccompaniedLoaded] = useState(false);

  const getModeOfTransportValue = (refData, formData) => {
    if (Object.keys(refData).length > 0)
      setTimeout(() => {
        setIsAccompaniedLoaded(true);
      }, 100);
    const value = refData?.modeOfTransport
      ?.find((r) => Number.parseInt(r.key) === Number.parseInt(formData?.TransportAndJourney?.Vehicle?.ModeOfTransport))
      ?.value?.toLowerCase?.();

    return value;
  };

  //Used in error check to determine if the vehicle is accompanied
  const isAccompanied = useMemo(() => {
    const modeOfTransportValue = getModeOfTransportValue(referenceData, data);
    return (
      modeOfTransportValue?.includes?.(' accompanied') ||
      modeOfTransportValue?.includes?.('euro shuttle') ||
      modeOfTransportValue?.includes?.('road')
    );
  }, [referenceData, data]);

  //Used in error check to determine if the vehicle is a road transport
  const isRoadTransport = useMemo(() => {
    const modeOfTransportValue = getModeOfTransportValue(referenceData, data);
    return modeOfTransportValue?.includes?.('road');
  }, [referenceData, data]);

  //Error functions used in error definitions
  const errorFunctions = useMemo(
    () => ({
      required: (val) => !!val,
      selected: (val) => !!val && val !== -1,
      dateNotInPast: (val, includeTime = true) => {
        // for some scenario we only want to ensure the date is not before today's date
        // we are using .endOf to ignore the time
        return includeTime ? !val?.isBefore() : !val?.endOf('day')?.isBefore();
      },
      dateBeforeArrival: (val) => {
        if (!val) return false;
        return !val.isAfter(data.TransportAndJourney.arrivalDateTime);
      },
      dateAfterDeparture: (d) => {
        if (!d) return false;
        return !d.isBefore(data.TransportAndJourney.departureDateTime);
      },
      transportDocumentsPresent: (rows) => rows?.length && rows.length > 0,
      trailerOrContainerNo: (trailerNo) => {
        return !(
          (!trailerNo || trailerNo?.trim?.()?.length === 0) &&
          (!data?.TransportAndJourney?.Vehicle?.containerNo ||
            data?.TransportAndJourney?.Vehicle?.containerNo?.trim?.()?.length === 0)
        );
      },
      isValidString: (val) => {
        // is allowed to be blank or empty
        if (!val || val === '') return true;

        // contains letters, numbers and basic punctuation only (including spaces)
        const regex = /^[\p{L}\p{N}\s.,!?'"()\-:;]+$/u;
        return regex.test(val);
      },
      requiredIfAccompanied: (val) => {
        if (isAccompanied) {
          return !!val;
        }
        return true;
      },
      requiredIfAccompaniedExceptRoad: (val) => {
        if (isAccompanied && !isRoadTransport) {
          return !!val;
        }
        return true;
      },
      selectedIfAccompanied: (val) => {
        if (isAccompanied) {
          return !!val && val !== -1;
        }
        return true;
      },
      selectedIfAccompaniedExceptRoad: (val) => {
        if (isAccompanied && !isRoadTransport) {
          return !!val && val !== -1;
        }
        return true;
      },
      requiredIfUnaccompanied: (val) => {
        if (!isAccompanied) {
          return !!val;
        }
        return true;
      },
      selectedIfUnaccompanied: (val) => {
        if (!isAccompanied) {
          return !!val && val !== -1;
        }
        return true;
      },
      carrierIfAccompanied: (v) => {
        return !((!v || v === '') && !isAccompanied);
      },
      validVesselIdentity: (val) => {
        if (isAccompanied && !isRoadTransport) {
          return /^[A-Za-z0-9]+$/.test(val);
        }
        return true;
      },
      validGBEORIOrBlank: async (eori) => {
        // must be a GB EORI number, or blank
        if (!eori || eori.trim().length === 0) return true;
        const res = await getEORIValidation(eori, 'true');
        return res.statusDescr !== 'In Valid';
      },
      validGBEORI: async (eori) => {
        // must be a GB EORI number
        const res = await getEORIValidation(eori, 'true');
        return res.statusDescr !== 'In Valid';
      },
      validEORIAndRequired: async (eori) => {
        // must be a valid EORI number and is required
        if (!eori || eori.trim().length === 0) return false;
        const res = await getEORIValidation(eori, eori.startsWith('GB') ? 'true' : 'false');
        return res.statusDescr !== 'In Valid';
      },
      validAnyEORI: async (eori) => {
        // must be a valid EORI number, or blank
        if (!eori || eori.trim().length === 0) return true;
        const res = await getEORIValidation(eori, eori.startsWith('GB') ? 'true' : 'false');
        return res.statusDescr !== 'In Valid';
      },
      validateNonGBEORI: async (eori, countryField = '', unaccompaniedOnly = false) => {
        if (unaccompaniedOnly && isAccompanied) {
          return true;
        }

        if (typeof countryField === 'string' && countryField !== '') {
          // countryField can be in format screen/modal/field
          // (i.e. TransportAndJourney/Carrier/Country) to reference a field
          // or it can also just be a country code (i.e. 'CH')
          const [screen, modal, field] = countryField.split('/');
          const fieldValue = modal && field ? data[screen]?.[modal]?.[field] : countryField;

          // if country is Switzerland or Norway, then EORI validation is not required
          if (fieldValue && !MANDATORY_EORI_COUNTRIES.includes(fieldValue)) {
            return true;
          }
        }

        //if EORI is empty or if it starts with 'GB', it is invalid, so we should return false
        if (!eori || eori.trim().length === 0 || eori.startsWith('GB')) return false;
        //else we should validate the EORI
        const res = await getEORIValidation(eori, 'false');
        return res.statusDescr !== 'In Valid';
      },
      validDescription: async (description) => {
        // description must not be prohibited
        const res = await getDescriptionValidation(description);

        if (res) {
          return res?.isStopWord !== 1;
        }
        return true;
      },
      validDangerousGoodsCodeOrBlank: (code) => {
        // dangerous goods code are always 4 digits ranging from 0001 to 9999
        if (!code || code === -1 || code.toString().trim().length === 0) return true;
        if (code === '0000') return false;

        const regex = /^\d{4}$/;
        return regex.test(code);
      },
      validTrailerNumber: (val) => {
        // trailer is only required if the vehicle is unaccompanied
        if (isAccompanied) {
          return true;
        }

        // field is required
        if (!val || val.trim().length === 0) {
          return false;
        }

        // find name of the equipment type selected
        if (!referenceData?.equipmentTypes) return false;
        const equipmentType = referenceData.equipmentTypes.find(
          (e) => e.key === data?.TransportAndJourney?.Vehicle?.equipmentTypeId
        );

        const isTrailer = !equipmentType?.value?.toLowerCase()?.includes('container');

        if (isTrailer) {
          if (val.length > 35) {
            return false;
          }

          const regex = /^[A-Z0-9\-\/,]+$/i;
          return regex.test(val);
        }

        return true;
      },
      validContainerNumber: (val) => {
        // container is only required if the vehicle is unaccompanied
        if (isAccompanied) {
          return true;
        }

        // field is required
        if (!val || val.trim().length === 0) {
          return false;
        }

        // find name of the equipment type selected
        if (!referenceData?.equipmentTypes) return false;
        const equipmentType = referenceData.equipmentTypes.find(
          (e) => e.key === data?.TransportAndJourney?.Vehicle?.equipmentTypeId
        );

        const isContainer = equipmentType?.value?.toLowerCase()?.includes('container');

        if (isContainer) {
          const regex = /^[A-Z]{4}\d{7}$/i;
          return regex.test(val);
        }

        return true;
      },
      positiveNumber: (val) => {
        const newValue = Math.abs(parseInt(val));
        return val ? newValue.toString() === val.toString() && newValue > 0 : true;
      },
      positiveNumberAndRequired: (val) => {
        const newValue = Math.abs(parseInt(val));
        return val ? newValue.toString() === val.toString() && val > 0 : false;
      },
      wholeNumber: (val) => {
        return Number.isInteger(parseFloat(val));
      },
      positiveFloatAndRequired: (val) => {
        const newValue = parseFloat(val);
        return val !== '' && !isNaN(newValue) && newValue > 0;
      },
      commodityCodeValidation: (code) => {
        // Commodity codes should be between 6 and 10 characters long
        if (!code || code.toString().trim().length === 0) return false;
        const codeStr = code.toString().trim();
        return codeStr.length >= 6 && codeStr.length <= 10;
      },
      piecesRequired: (numPieces, line) => {
        if (!referenceData?.packagesWithPiecesRequired) return false;
        const packageType = line?.packageType;
        const piecesFieldRequired = referenceData.packagesWithPiecesRequired.includes(packageType?.toString());

        const newValue = Math.abs(parseInt(numPieces));
        const positiveNumberAndRequired = numPieces
          ? newValue.toString() === numPieces.toString() && numPieces > 0
          : false;

        if (piecesFieldRequired || numPieces) {
          return positiveNumberAndRequired;
        }

        return true;
      },
      packageMarksRequired: (marks, line) => {
        if (!referenceData?.packagesWithPiecesDisabled) return false;
        const packageType = line?.packageType;
        const marksFieldDisabled = referenceData.packagesWithPiecesDisabled.includes(packageType?.toString());

        if (marksFieldDisabled) {
          return true;
        }

        return !!marks;
      },
      isAllowedDutchArrivalPort: (val) => {
        // Only unaccompanied mode of transport can select Dutch ports as arrival ports

        const arrivalPortId = data?.TransportAndJourney?.portOfArrival;
        const arrivalPortCountry = getEntryPortCountryCode(referenceData?.entryPorts, arrivalPortId);

        return !(arrivalPortCountry === 'NL' && isAccompanied);
      },
      validENI: (val, isActive = false) => {
        const transportType = isActive
          ? data?.TransportAndJourney?.Vehicle?.activeTransportTypeId
          : data?.TransportAndJourney?.Vehicle?.transportTypeId;

        if (transportType === 80) {
          return val.length === 8 && /^\d+$/.test(val);
        }
        return true;
      },
      validIMO: (val, isActive = false) => {
        const transportType = isActive
          ? data?.TransportAndJourney?.Vehicle?.activeTransportTypeId
          : data?.TransportAndJourney?.Vehicle?.transportTypeId;

        if (transportType === 10) {
          return val?.toUpperCase()?.startsWith('IMO') && val.length === 10;
        }
        return true;
      },
      validTrainNumber: (val, isActive = false) => {
        const transportType = isActive
          ? data?.TransportAndJourney?.Vehicle?.activeTransportTypeId
          : data?.TransportAndJourney?.Vehicle?.transportTypeId;

        if (transportType === 21) {
          return val?.length <= 35;
        }
        return true;
      },
      noSpecialCharacters: (val) => {
        if (val?.trim() === '') {
          return true;
        }
        return /^[A-Za-z0-9]+$/.test(val);
      },
      consignmentCheckMultiField: (val, consignment, otherField, isSelection = false) => {
        //for multi fields, this function checks if another field is empty or not
        if (consignment) {
          const otherFieldValue = consignment[otherField];
          const isEmpty = isSelection ? val === -1 : val?.trim() === '';
          if ((isEmpty || !val) && otherFieldValue && otherFieldValue !== '' && otherFieldValue !== -1) {
            return false;
          }
        }
        return true;
      },
      consignmentHasPlaceOfDelivery: (val, consignment) => {
        const country = consignment?.placeOfDeliveryCountry;
        const placeName = consignment?.placeOfDeliveryPlaceName;

        const valueIsEmpty = !val || val === '' || val === -1;
        const placeOfDeliveryIsComplete = country && placeName && country !== -1 && placeName?.trim() !== '';

        if (valueIsEmpty && placeOfDeliveryIsComplete) {
          return false;
        }
        return true;
      },
      consignmentHasPlaceOfAcceptance: (val, consignment) => {
        const country = consignment?.placeOfAcceptanceCountry;
        const placeName = consignment?.placeOfAcceptancePlaceName;

        const valueIsEmpty = !val || val === '' || val === -1;
        const placeOfAcceptanceIsComplete = country && placeName && country !== -1 && placeName?.trim() !== '';

        if (valueIsEmpty && placeOfAcceptanceIsComplete) {
          return false;
        }
        return true;
      },
      routeContainsArrivalPort: () => {
        // find the country code of the arrival port
        const arrivalPortId = data?.TransportAndJourney?.portOfArrival;
        const arrivalPortCountry = getEntryPortCountryCode(referenceData?.entryPorts, arrivalPortId);

        // find the country codes of all countries in the route
        const routeValues = Object.values(data?.TransportAndJourney?.Route);
        const anyStops = routeValues?.filter((s) => !!s && s !== -1);
        const resolvedStops = anyStops.map((s) => referenceData.countryOfOriginMap?.find((c) => c.key === s)?.value);

        return resolvedStops.includes(arrivalPortCountry);
      },
    }),
    [data, referenceData, currentConsignment, isAccompanied]
  );

  //Error definitions for each field, and modal. Special logic for consignment lines.
  const errorDefinitions = useMemo(
    () => ({
      TransportAndJourney: [
        { id: 'jobReference', func: errorFunctions.required, errorText: 'Job Reference is required.' },
        {
          id: 'Vehicle',
          type: 'modal',
          errorText: 'There are errors with the transport identity.',
          children: [
            { id: 'ModeOfTransport', func: errorFunctions.selected, errorText: 'Mode of Transport is required.' },
            {
              id: 'ModeOfTransport',
              func: errorFunctions.isAllowedDutchArrivalPort,
              errorText: 'Please select “UNACCOMPANIED” for arrival at NL ports.',
            },
            { id: 'vehicleID', func: errorFunctions.required, errorText: 'Transport Identity is required.' },
            {
              id: 'vehicleID',
              func: errorFunctions.validENI,
              errorText: 'Transport Identity must be 8 characters (only numbers).',
            },
            {
              id: 'vehicleID',
              func: errorFunctions.validIMO,
              errorText: 'Transport Identity must be 10 characters and begin with IMO.',
            },
            {
              id: 'vehicleID',
              func: errorFunctions.validTrainNumber,
              errorText: 'Transport Identity must be less than 35 characters.',
            },
            {
              id: 'vehicleID',
              func: errorFunctions.noSpecialCharacters,
              errorText: 'Transport Identity must contain no spaces or special characters.',
            },
            { id: 'transportTypeId', func: errorFunctions.selected, errorText: 'Transport Type is required.' },
            { id: 'Nationality', func: errorFunctions.selected, errorText: 'Nationality is required.' },
            {
              id: 'vesselIdentity',
              func: errorFunctions.requiredIfAccompaniedExceptRoad,
              errorText: 'Vessel Identity is required.',
            },
            {
              id: 'vesselIdentity',
              func: (val) => errorFunctions.validENI(val, true),
              errorText: 'Transport Identity must be 8 characters (only numbers).',
            },
            {
              id: 'vesselIdentity',
              func: (val) => errorFunctions.validIMO(val, true),
              errorText: 'Transport Identity must be 10 characters and begin with IMO.',
            },
            {
              id: 'vesselIdentity',
              func: (val) => errorFunctions.validTrainNumber(val, true),
              errorText: 'Transport Identity must be less than 35 characters.',
            },
            {
              id: 'vesselIdentity',
              func: errorFunctions.validVesselIdentity,
              errorText: 'Vessel Identity must not contain spaces or special characters.',
            },
            {
              id: 'activeTransportTypeId',
              func: errorFunctions.selectedIfAccompaniedExceptRoad,
              errorText: 'Vessel Transport Type is required.',
            },
            {
              id: 'vesselNationalityId',
              func: errorFunctions.selectedIfAccompaniedExceptRoad,
              errorText: 'Vessel Nationality is required.',
            },
            {
              id: 'trailerOrContainerNumber',
              func: errorFunctions.validContainerNumber,
              errorText: 'Container Number must be in format ZZZZ9999999.',
            },
            {
              id: 'trailerOrContainerNumber',
              func: errorFunctions.validTrailerNumber,
              errorText: 'Container Number must be less than 35 characters and contain no special characters.',
            },
            {
              id: 'trailerOrContainerNumber',
              func: errorFunctions.requiredIfUnaccompanied,
              errorText: 'Trailer/Container is required.',
            },
            {
              id: 'equipmentTypeId',
              func: errorFunctions.selectedIfUnaccompanied,
              errorText: 'Equipment type is required.',
            },
            { id: 'sealNumber', func: errorFunctions.requiredIfUnaccompanied, errorText: 'Seal Number is required.' },
          ],
        },
        {
          id: 'Consignor',
          type: 'modal',
          errorText: 'There are errors with the groupage consignor.',
          children: [
            {
              id: 'eori',
              func: errorFunctions.validAnyEORI,
              isAsync: true,
              errorText: 'Consignor must have a valid EORI number.',
            },
            {
              id: 'Country',
              func: errorFunctions.selectedIfUnaccompanied,
              errorText: 'Consignor Country is required.',
            },
            {
              id: 'companyName',
              func: errorFunctions.requiredIfUnaccompanied,
              errorText: 'Consignor Company Name is required.',
            },
            {
              id: 'streetNumber',
              func: errorFunctions.requiredIfUnaccompanied,
              errorText: 'Consignor Street Number is required.',
            },
            {
              id: 'streetAddress',
              func: errorFunctions.requiredIfUnaccompanied,
              errorText: 'Consignor Street Address is required.',
            },
            {
              id: 'townCity',
              func: errorFunctions.requiredIfUnaccompanied,
              errorText: 'Consignor Town / City is required.',
            },
            {
              id: 'postCode',
              func: errorFunctions.requiredIfUnaccompanied,
              errorText: 'Consignor Postcode is required.',
            },
          ],
        },
        {
          id: 'Consignee',
          type: 'modal',
          errorText: 'There are errors with the groupage consignee.',
          children: [
            {
              id: 'eori',
              func: (eori) => {
                return errorFunctions.validateNonGBEORI(eori, 'TransportAndJourney/Consignee/Country', true);
              },
              isAsync: true,
              errorText: 'Consignee must have a valid EU EORI number.',
            },
            {
              id: 'Country',
              func: errorFunctions.selectedIfUnaccompanied,
              errorText: 'Consignee Country is required.',
            },
            {
              id: 'companyName',
              func: errorFunctions.requiredIfUnaccompanied,
              errorText: 'Consignee Company Name is required.',
            },
            {
              id: 'streetNumber',
              func: errorFunctions.requiredIfUnaccompanied,
              errorText: 'Consignee Street Number is required.',
            },
            {
              id: 'streetAddress',
              func: errorFunctions.requiredIfUnaccompanied,
              errorText: 'Consignee Street Address is required.',
            },
            {
              id: 'townCity',
              func: errorFunctions.requiredIfUnaccompanied,
              errorText: 'Consignee Town / City is required.',
            },
            {
              id: 'postCode',
              func: errorFunctions.requiredIfUnaccompanied,
              errorText: 'Consignee Postcode is required.',
            },
          ],
        },
        {
          id: 'Carrier',
          type: 'modal',
          errorText: 'There are errors with the carrier.',
          children: [
            {
              id: 'eori',
              func: (eori) => errorFunctions.validateNonGBEORI(eori, 'TransportAndJourney/Carrier/Country'),
              isAsync: true,
              errorText: 'Carrier must have a valid EU EORI number.',
            },
            { id: 'Country', func: errorFunctions.selected, errorText: 'Country is required.' },
            { id: 'companyName', func: errorFunctions.required, errorText: 'Company Name is required.' },
            { id: 'streetNumber', func: errorFunctions.required, errorText: 'Street Number is required.' },
            { id: 'streetAddress', func: errorFunctions.required, errorText: 'Street Address is required.' },
            { id: 'townCity', func: errorFunctions.required, errorText: 'Town / City is required.' },
            { id: 'postCode', func: errorFunctions.required, errorText: 'Postcode is required.' },
          ],
        },
        {
          id: 'portOfDeparture',
          func: errorFunctions.isValidString,
          errorText: 'Port of Departure must contain only letters, numbers and basic punctuation.',
        },
        {
          id: 'departure',
          type: 'dateTime',
          func: (val) => errorFunctions.dateNotInPast(val, false),
          errorText: 'Departure date cannot be in the past.',
        },
        {
          id: 'departure',
          type: 'dateTime',
          func: errorFunctions.dateBeforeArrival,
          errorText: 'Date must be before arrival date.',
        },
        { id: 'portOfArrival', func: errorFunctions.selected, errorText: 'Port of Arrival is required.' },
        {
          id: 'arrival',
          type: 'dateTime',
          func: errorFunctions.dateNotInPast,
          errorText: 'Arrival date and time cannot be in the past.',
        },
        {
          id: 'arrival',
          type: 'dateTime',
          func: errorFunctions.dateAfterDeparture,
          errorText: 'Date must be after departure date.',
        },
        {
          id: 'Route',
          type: 'modal',
          errorText: 'There are errors with the route.',
          children: [
            { id: 'departureCountry', func: errorFunctions.selected, errorText: 'Departure country is required.' },
            { id: 'destinationCountry', func: errorFunctions.selected, errorText: 'Destination country is required.' },
            {
              id: 'destinationCountry',
              func: errorFunctions.routeContainsArrivalPort,
              errorText: 'The Port of Arrival country code must be included in the Countries of Routing.',
            },
          ],
        },
        { id: 'transportCharges', func: errorFunctions.selected, errorText: 'Transport Charges MOP is required.' },
        {
          id: 'placeOfLoading',
          type: 'multiField',
          errorText: 'There are errors with the place of loading.',
          children: [
            { id: 'placeOfLoadingCountry', func: errorFunctions.selected, errorText: 'Country is required.' },
            { id: 'placeOfLoadingName', func: errorFunctions.required, errorText: 'Place name is required.' },
          ],
        },
        {
          id: 'placeOfUnloading',
          type: 'multiField',
          errorText: 'There are errors with the place of unloading.',
          children: [
            { id: 'placeOfUnloadingCountry', func: errorFunctions.selected, errorText: 'Country is required.' },
            { id: 'placeOfUnloadingName', func: errorFunctions.required, errorText: 'Place name is required.' },
          ],
        },
        {
          id: 'placeOfAcceptance',
          type: 'multiField',
          errorText: 'There are errors with the place of acceptance.',
          children: [
            { id: 'placeOfAcceptanceCountry', func: errorFunctions.selected, errorText: 'Country is required.' },
            { id: 'placeOfAcceptanceName', func: errorFunctions.required, errorText: 'Place name is required.' },
          ],
        },
        {
          id: 'placeOfDelivery',
          type: 'multiField',
          errorText: 'There are errors with the place of delivery.',
          children: [
            { id: 'placeOfDeliveryCountry', func: errorFunctions.selected, errorText: 'Country is required.' },
            { id: 'placeOfDeliveryName', func: errorFunctions.required, errorText: 'Place name is required.' },
          ],
        },
        {
          id: 'transportDocument',
          type: 'multiField',
          errorText: 'There are errors with the transport document.',
          children: [
            { id: 'transportDocTypeId', func: errorFunctions.selected, errorText: 'Document type is required.' },
            {
              id: 'transportDocReference',
              func: errorFunctions.required,
              errorText: 'Document reference is required.',
            },
          ],
        },
      ],
      Consignments: [
        { id: 'consignmentReference', func: errorFunctions.required, errorText: 'Consignment Reference is required' },
        {
          id: 'transportDocument',
          type: 'multiField',
          errorText: 'There are errors with the Transport Document.',
          children: [
            {
              id: 'transportDocTypeId',
              func: (val, consignment) =>
                errorFunctions.consignmentCheckMultiField(val, consignment, 'transportDocReference', true),
              errorText: 'Transport Document Type must be entered if Transport Document Reference is filled.',
            },
            {
              id: 'transportDocReference',
              func: (val, consignment) =>
                errorFunctions.consignmentCheckMultiField(val, consignment, 'transportDocTypeId'),
              errorText: 'Transport Document Reference must be entered if Transport Document Type is filled.',
            },
          ],
        },
        {
          id: 'placeOfAcceptance',
          type: 'multiField',
          errorText: 'There are errors with the place of Acceptance.',
          children: [
            {
              id: 'placeOfAcceptanceCountry',
              func: (val, consignment) =>
                errorFunctions.consignmentCheckMultiField(val, consignment, 'placeOfAcceptancePlaceName', true),
              errorText: 'Country is required as you have entered a place name.',
            },
            {
              id: 'placeOfAcceptancePlaceName',
              func: (val, consignment) =>
                errorFunctions.consignmentCheckMultiField(val, consignment, 'placeOfAcceptanceCountry'),
              errorText: 'Place name is required as you have entered a country.',
            },
            {
              id: 'placeOfAcceptanceCountry',
              func: errorFunctions.consignmentHasPlaceOfDelivery,
              errorText: 'Place of Acceptance must be entered if Place of Delivery is filled.',
            },
          ],
        },
        {
          id: 'placeOfDelivery',
          type: 'multiField',
          errorText: 'There are errors with the place of Delivery.',
          children: [
            {
              id: 'placeOfDeliveryCountry',
              func: (val, consignment) =>
                errorFunctions.consignmentCheckMultiField(val, consignment, 'placeOfDeliveryPlaceName', true),
              errorText: 'Country is required as you have entered a place name.',
            },
            {
              id: 'placeOfDeliveryPlaceName',
              func: (val, consignment) =>
                errorFunctions.consignmentCheckMultiField(val, consignment, 'placeOfDeliveryCountry'),
              errorText: 'Place name is required as you have entered a country.',
            },
            {
              id: 'placeOfDeliveryCountry',
              func: errorFunctions.consignmentHasPlaceOfAcceptance,
              errorText: 'Place of Delivery must be entered if Place of Acceptance is filled.',
            },
          ],
        },
        {
          id: 'Consignor',
          type: 'modal',
          errorText: 'There are errors with the consignor.',
          children: [
            {
              id: 'eori',
              func: errorFunctions.validAnyEORI,
              isAsync: true,
              errorText: 'Must be a valid EORI number.',
            },
            { id: 'Country', func: errorFunctions.selected, errorText: 'Country is required.' },
            { id: 'companyName', func: errorFunctions.required, errorText: 'Company Name is required.' },
            { id: 'streetAddress', func: errorFunctions.required, errorText: 'Street Address is required.' },
            { id: 'streetNumber', func: errorFunctions.required, errorText: 'Street Number is required.' },
            { id: 'townCity', func: errorFunctions.required, errorText: 'Town / City is required.' },
            { id: 'postCode', func: errorFunctions.required, errorText: 'Postcode is required.' },
          ],
        },
        {
          id: 'Seller',
          type: 'modal',
          errorText: 'There are errors with the seller.',
          children: [
            {
              id: 'eori',
              func: errorFunctions.validAnyEORI,
              isAsync: true,
              errorText: 'Must be a valid EORI number.',
            },
            { id: 'Country', func: errorFunctions.selected, errorText: 'Country is required.' },
            { id: 'companyName', func: errorFunctions.required, errorText: 'Company Name is required.' },
            { id: 'streetAddress', func: errorFunctions.required, errorText: 'Street Address is required.' },
            { id: 'streetNumber', func: errorFunctions.required, errorText: 'Street Number is required.' },
            { id: 'townCity', func: errorFunctions.required, errorText: 'Town / City is required.' },
            { id: 'postCode', func: errorFunctions.required, errorText: 'Postcode is required.' },
          ],
        },
        {
          id: 'Consignee',
          type: 'modal',
          errorText: 'There are errors with the consignee.',
          children: [
            {
              id: 'eori',
              func: (eori, consignment) =>
                errorFunctions.validateNonGBEORI(
                  eori,
                  consignment?._Consignee?.Country || consignment?.Consignee?.Country
                ),
              isAsync: true,
              errorText: 'Must be a valid EU EORI number.',
            },
            { id: 'Country', func: errorFunctions.selected, errorText: 'Country is required.' },
            { id: 'companyName', func: errorFunctions.required, errorText: 'Company Name is required.' },
            { id: 'streetAddress', func: errorFunctions.required, errorText: 'Street Address is required.' },
            { id: 'streetNumber', func: errorFunctions.required, errorText: 'Street Number is required.' },
            { id: 'townCity', func: errorFunctions.required, errorText: 'Town / City is required.' },
            { id: 'postCode', func: errorFunctions.required, errorText: 'Postcode is required.' },
          ],
        },
        {
          id: 'Buyer',
          type: 'modal',
          errorText: 'There are errors with the buyer.',
          children: [
            {
              id: 'eori',
              func: (eori, consignment) =>
                errorFunctions.validateNonGBEORI(eori, consignment?._Buyer?.Country || consignment?.Buyer?.Country),
              isAsync: true,
              errorText: 'Must be a valid EU EORI number.',
            },
            { id: 'Country', func: errorFunctions.selected, errorText: 'Country is required.' },
            { id: 'companyName', func: errorFunctions.required, errorText: 'Company Name is required.' },
            { id: 'streetAddress', func: errorFunctions.required, errorText: 'Street Address is required.' },
            { id: 'streetNumber', func: errorFunctions.required, errorText: 'Street Number is required.' },
            { id: 'townCity', func: errorFunctions.required, errorText: 'Town / City is required.' },
            { id: 'postCode', func: errorFunctions.required, errorText: 'Postcode is required.' },
          ],
        },
        {
          id: 'lines',
          type: 'consignmentLines',
          errorText: 'There are errors with the consignment lines.',
          errorTextLocal: 'There are errors with this consignment line.',
          children: [
            {
              id: 'commodityCode',
              func: errorFunctions.commodityCodeValidation,
              errorText: 'Commodity Codes should have between 6 and 10 characters',
            },
            { id: 'goodsDescription', func: errorFunctions.required, errorText: 'Goods Description is required.' },
            {
              id: 'goodsDescription',
              func: errorFunctions.validDescription,
              isAsync: true,
              errorText: 'Goods Description includes a prohibited word from the stop list.',
            },
            { id: 'packageType', func: errorFunctions.selected, errorText: 'Type Of Packaging is required.' },
            {
              id: 'numPieces',
              func: errorFunctions.packageMarksRequired,
              errorText: 'Number of Pieces is required.',
            },
            {
              id: 'numPieces',
              func: errorFunctions.positiveNumber,
              errorText: 'Number of Pieces must be a whole number greater than zero.',
            },
            {
              id: 'packagingMarks',
              func: errorFunctions.packageMarksRequired,
              errorText: 'Packaging Marks is required.',
            },
            {
              id: 'grossWeight',
              func: errorFunctions.positiveFloatAndRequired,
              errorText: 'Gross weight must be a number greater than or equal to zero.',
            },
            {
              id: 'dangerousGoods',
              func: errorFunctions.validDangerousGoodsCodeOrBlank,
              errorText: 'UN Dangerous Goods Description must be selected from the drop down or a 4 digit code.',
            },
          ],
        },
        { id: 'consignmentLinesPresent', errorText: 'Consignment must have at least 1 line' },
      ],
    }),
    [errorFunctions]
  );

  //Check whether to check errors at the start of the page load
  useEffect(() => {
    const check = async () => {
      if (state?.headerId && toRunInitialErrorCheck && data && !data.initialLoad && isAccompaniedLoaded) {
        runInitialErrorCheck();
        setToRunInitialErrorCheck(false);
      }
    };
    check();
  }, [toRunInitialErrorCheck, data, state, isAccompaniedLoaded]);

  //Check which pages to check, based on if they have data, and if so, check for errors.
  const runInitialErrorCheck = async () => {
    const TransportAndJourneyPresent =
      data['TransportAndJourney'] && Object.keys(data['TransportAndJourney']).length > 0;
    const ConsignmentsPresent = data['Consignments'] && data['Consignments'].length > 0;

    let TransportAndJourneyValidation = null;
    let TransportAndJourneyHMRC = null;
    let ConsignmentsValidation = null;
    let ConsignmentsHMRC = null;

    // Check for errors in Transport and Journey if present and if shipment is editable
    if ((TransportAndJourneyPresent || ConsignmentsPresent) && isEditable) {
      const TransportAndJourney = await checkErrorsStandard('TransportAndJourney', true);
      TransportAndJourneyValidation = TransportAndJourney._errors;
      TransportAndJourneyHMRC = TransportAndJourney._hmrcErrors;
    }

    if (ConsignmentsPresent) {
      const consignmentErrors = [];
      for (let i = 0; i < data?.Consignments?.length; i++) {
        const consignment = data?.Consignments[i];
        const res = await checkErrorsConsignment(consignment);
        consignmentErrors.push(res);
      }
      ConsignmentsValidation = consignmentErrors.map((c) => c._errors);
      ConsignmentsHMRC = consignmentErrors.map((c) => c._errorsHMRC);
    }

    setErrors({
      ...(TransportAndJourneyValidation && { TransportAndJourney: TransportAndJourneyValidation }),
      Consignments: ConsignmentsValidation,
    });
    setErrorsHMRC({
      Consignments: ConsignmentsHMRC,
      ...(TransportAndJourneyHMRC && { TransportAndJourney: TransportAndJourneyHMRC }),
    });
    setInitialErrorCheckRun(true);
  };

  //================================================================================================
  //========================Main error checking functions=========================================
  //================================================================================================
  //Please change the error definitions above instead of changing this code.
  //Please consult Ben before changing the below code.

  //Helper function to find HMRC error for a specific consignment line
  const findMatchingConsignmentError = (consignmentId, itemId, columnName) => {
    const matchingConsignment = data.ErrorCodeColumnMapping?.Consignments?.find?.((c) => c.id === consignmentId);
    const matchingLine = matchingConsignment?.lines?.find?.((l) => l.id === itemId);
    if (!matchingLine) {
      return null;
    }
    const lineErrors = Object.keys(matchingLine);
    return lineErrors.includes(columnName) ? matchingLine[columnName] : null;
  };

  //Used for checking errors on any modals without closing the modal
  const checkErrorsLocal = useCallback(
    async (modal, screen, skipLocal) => {
      let _errors = {};
      const errorToCheck = errorDefinitions[screen].filter((e) => e.id === modal && e.type === 'modal')[0];
      let allPassed = true;
      for (const element of errorToCheck.children) {
        const subErr = element;
        let passed;
        if (subErr.isAsync) passed = await subErr.func(data[screen]?.[(skipLocal ? '' : '_') + modal]?.[subErr.id]);
        else passed = subErr.func(data[screen]?.[(skipLocal ? '' : '_') + modal]?.[subErr.id]);
        if (!passed) {
          allPassed = false;
          if (!_errors?.[modal]) _errors[modal] = { [subErr.id]: { msg: subErr.errorText } };
          else _errors[modal][subErr.id] = { msg: subErr.errorText };
        }
      }
      if (!allPassed) _errors[modal].modalError = { msg: errorToCheck.errorText };

      //Return the value
      if (Object.keys(_errors).length === 0) return null;

      return _errors;
    },
    [data, errors, errorsLocal]
  );

  //Used for checking errors on the consignment modals without closing the modal
  const checkErrorsConsignmentLocal = useCallback(
    async (modal, skipLocal) => {
      let _errors = {};
      const errorToCheck = errorDefinitions['Consignments'].filter((e) => e.id === modal && e.type === 'modal')[0];
      let allPassed = true;
      for (const element of errorToCheck.children) {
        const subErr = element;
        let passed;
        if (subErr.isAsync)
          passed = await subErr.func(
            currentConsignment?.[(skipLocal ? '' : '_') + modal]?.[subErr.id],
            currentConsignment
          );
        else
          passed = subErr.func(currentConsignment?.[(skipLocal ? '' : '_') + modal]?.[subErr.id], currentConsignment);
        if (!passed) {
          allPassed = false;
          if (!_errors?.[modal]) _errors[modal] = { [subErr.id]: { msg: subErr.errorText } };
          else _errors[modal][subErr.id] = { msg: subErr.errorText };
        }
      }
      if (!allPassed) _errors[modal].modalError = { msg: errorToCheck.errorText };

      //Return the value
      if (Object.keys(_errors).length === 0) return null;

      return _errors;
    },
    [currentConsignment, currentConsignmentErrors, errorsLocal]
  );

  const checkErrorsConsignment = useCallback(
    async (consignmentToCheck) => {
      const consignmentToUse = consignmentToCheck || currentConsignment;
      let _errors = { id: consignmentToUse?.id, lines: [] };
      let _errorsHMRC = { id: consignmentToUse?.id, lines: [] };
      const errorsToCheck = errorDefinitions['Consignments'];
      for (const err of errorsToCheck) {
        if (err.type === 'modal') {
          let allPassed = true;
          if (err.id === 'Seller' || err.id === 'Buyer') {
            // if the consignmentToUse does not have the err.id, we skip the error check for that modal, as these are not mandatory
            if (!consignmentToUse?.[err.id]) continue;
          }
          for (const subErr of err.children) {
            let passed;
            if (subErr.isAsync) passed = await subErr.func(consignmentToUse?.[err.id]?.[subErr.id], consignmentToUse);
            else passed = subErr.func(consignmentToUse?.[err.id]?.[subErr.id], consignmentToUse);
            if (!passed) {
              allPassed = false;
              if (!_errors?.[err.id]) _errors[err.id] = { [subErr.id]: { msg: subErr.errorText } };
              else _errors[err.id][subErr.id] = { msg: subErr.errorText };
            }
          }
          if (!allPassed) _errors[err.id].modalError = { msg: err.errorText };
        } else if (err.type === 'consignmentLines') {
          let allPassedValidation = true;
          let allPassedHMRC = true;
          for (const l in consignmentToUse?.lines) {
            const line = consignmentToUse?.lines[l];
            let linePassedValidation = true;
            let linePassedHMRC = true;
            for (const s in err.children) {
              const subErr = err.children[s];
              const passed = subErr.isAsync
                ? await subErr.func(line?.[subErr.id], line)
                : subErr.func(line?.[subErr.id], line);
              const matchingErrorLine = _errors.lines.find((l) => l.id === line.id);
              if (!passed) {
                linePassedValidation = false;
                allPassedValidation = false;
                if (!matchingErrorLine) {
                  _errors = {
                    ..._errors,
                    lines: [..._errors.lines, { id: line.id, [subErr.id]: { msg: subErr.errorText } }],
                  };
                } else matchingErrorLine[subErr.id] = { msg: subErr.errorText };
              } else {
                const HMRCError = findMatchingConsignmentError(consignmentToUse.id, line.id, subErr.id);
                const matchingErrorLine = _errorsHMRC.lines.find((l) => l.id === line.id);
                if (HMRCError) {
                  linePassedHMRC = false;
                  allPassedHMRC = false;
                  if (!matchingErrorLine) {
                    _errorsHMRC = {
                      ..._errorsHMRC,
                      lines: [
                        ..._errorsHMRC.lines,
                        {
                          id: line.id,
                          [subErr.id]: {
                            msg: 'There are errors with this field',
                            msgs: HMRCError.map((e) => e.scenario),
                          },
                        },
                      ],
                    };
                  } else matchingErrorLine[subErr.id] = { msg: HMRCError.scenario };
                }
              }
            }
            if (!linePassedValidation) {
              const matchingErrorLine = _errors.lines.find((l) => l.id === line.id);
              matchingErrorLine.lineError = { msg: err.errorTextLocal };
            }
            if (!linePassedHMRC) {
              const matchingErrorLine = _errorsHMRC.lines.find((l) => l.id === line.id);
              matchingErrorLine.lineError = { msg: err.errorTextLocal };
            }
          }
          if (!allPassedValidation) _errors.lineErrors = { msg: err.errorText };
          if (!allPassedHMRC) _errorsHMRC.lineErrors = { msg: err.errorText };
        } else if (err.id === 'consignmentLinesPresent') {
          const passed = consignmentToUse?.lines?.length > 0;
          if (!passed) _errors[err.id] = { msg: err.errorText };
        } else if (err.type === 'multiField') {
          // for components with multiple fields, which are not in a modal
          let allPassed = true;
          err.children.forEach((subErr) => {
            const passed = subErr.func(consignmentToUse?.[subErr.id], consignmentToUse);
            if (!passed) {
              allPassed = false;
              if (!_errors?.[err.id]) _errors[err.id] = { [subErr.id]: { msg: subErr.errorText } };
              else _errors[err.id][subErr.id] = { msg: subErr.errorText };
            }
          });
          if (!allPassed) _errors[err.id].modalError = { msg: err.errorText };
        } else {
          const passed = err.func(consignmentToUse?.[err.id], consignmentToUse);
          if (!passed) _errors[err.id] = { msg: err.errorText };
        }
      }
      if (!consignmentToCheck) {
        setCurrentConsignmentErrors(_errors);
        setCurrentConsignmentHMRCErrors(_errorsHMRC);
      } else {
        return { _errors, _errorsHMRC };
      }
    },
    [currentConsignment, errorDefinitions, referenceData]
  );

  const checkErrorsStandard = useCallback(
    async (screen, returnInsteadOfSet) => {
      const _errors = {};
      const _hmrcErrors = {};
      const errorsToCheck = errorDefinitions[screen];

      for (const err of errorsToCheck) {
        if (err.type === 'modal') {
          let allPassed = true;
          for (const subErr of err.children) {
            const passed = subErr.isAsync
              ? await subErr.func(data?.[screen]?.[err.id]?.[subErr.id])
              : subErr.func(data?.[screen]?.[err.id]?.[subErr.id]);
            if (!passed) {
              allPassed = false;
              if (!_errors?.[err.id]) _errors[err.id] = { [subErr.id]: { msg: subErr.errorText } };
              else _errors[err.id][subErr.id] = { msg: subErr.errorText };
            }
          }
          if (!allPassed) _errors[err.id].modalError = { msg: err.errorText };
        } else if (err.type === 'multiField') {
          // for components with multiple fields, which are not in a modal
          let allPassed = true;
          err.children.forEach((subErr) => {
            const passed = subErr.func(data?.[screen]?.[subErr.id]);
            if (!passed) {
              allPassed = false;
              if (!_errors?.[err.id]) _errors[err.id] = { [subErr.id]: { msg: subErr.errorText } };
              else _errors[err.id][subErr.id] = { msg: subErr.errorText };
            }
          });
          if (!allPassed) _errors[err.id].modalError = { msg: err.errorText };
        } else if (err.type === 'date') {
          const passed = err.func(data?.[screen]?.[err.id + 'Date']);
          if (!passed) _errors[err.id] = { msg: err.errorText };
        } else if (err.type === 'dateTime') {
          const passed = err.func(data?.[screen]?.[err.id + 'DateTime'], data?.[screen]?.['departureDate']);
          if (!passed) _errors[err.id] = { msg: err.errorText };
        } else {
          let passed;
          if (err.isAsync) {
            passed = await err.func(data?.[screen]?.[err.id]);
          } else {
            passed = err.func(data?.[screen]?.[err.id]);
          }
          if (!passed) _errors[err.id] = { msg: err.errorText };
          else if (data.ErrorCodeColumnMapping?.[screen]?.[err.id]) {
            _hmrcErrors[err.id] = {
              msg: 'There are errors with a field',
              msgLocal: 'There are errors with this field',
              msgs: data.ErrorCodeColumnMapping?.[screen]?.[err.id].map((e) => e.scenario),
            };
          }
        }
      }
      if (returnInsteadOfSet) return { _errors, _hmrcErrors };
      setErrors((prev) => ({ ...prev, [screen]: _errors }));
    },
    [data, errorDefinitions]
  );

  //Call appropriate error check function based on screen
  const checkErrors = useCallback(
    async (screen) => {
      if (screen !== 'Consignments') return await checkErrorsStandard(screen);
      return await checkErrorsConsignment();
    },
    [checkErrorsStandard, checkErrorsConsignment]
  );

  //===============================================================================
  //===================END OF MAIN ERROR CHECKING FUNCTIONS========================
  //===============================================================================

  //Used to show the orange warning triangles on the tabs
  const tabValidationErrors = useMemo(
    () => ({
      ...errors,
      ...((errors.Consignments &&
        errors.Consignments.length > 0 &&
        errors.Consignments.filter((c) => Object.keys(c).filter((k) => k !== 'lines' && k !== 'id').length > 0).length >
          0) ||
      (data.Consignments?.length < 1 && consignmentsVisited)
        ? {
            Consignments: {
              modalError: {
                msg:
                  data.Consignments?.length > 0
                    ? 'There are errors with the consignments.'
                    : 'There must be at least 1 consignment.',
              },
            },
          }
        : { Consignments: {} }),
      ...(currentConsignmentErrors &&
      Object.keys(currentConsignmentErrors).filter((k) => k !== 'lines' && k !== 'id').length > 0
        ? { AddingConsignment: currentConsignmentErrors, UpdatingConsignment: currentConsignmentErrors }
        : { AddingConsignment: {}, UpdatingConsignment: {} }),
    }),
    [errors, data, currentConsignmentErrors, consignmentsVisited]
  );

  //Used to show the red warning triangles on the tabs
  const HMRCtabValidationErrors = useMemo(
    () => ({
      ...errorsHMRC,
      ...(errorsHMRC.Consignments &&
      errorsHMRC.Consignments.length > 0 &&
      errorsHMRC.Consignments.filter((c) => Object.keys(c).filter((k) => k !== 'lines' && k !== 'id').length > 0)
        .length > 0
        ? { Consignments: { modalError: { msg: 'There are HMRC errors with the consignments.' } } }
        : { Consignments: undefined }),
      ...(currentConsignmentHMRCErrors &&
      Object.keys(currentConsignmentHMRCErrors).filter((k) => k !== 'lines' && k !== 'id').length > 0
        ? { AddingConsignment: currentConsignmentHMRCErrors, UpdatingConsignment: currentConsignmentHMRCErrors }
        : { AddingConsignment: {}, UpdatingConsignment: {} }),
    }),
    [errorsHMRC, data, currentConsignmentErrors]
  );

  const hasErrors = useCallback(
    (screen) => {
      return tabValidationErrors[screen] && Object.keys(tabValidationErrors[screen]).length > 0;
    },
    [tabValidationErrors]
  );

  //Show the fix or continue modal if there are errors. Some code related to this in the main container
  const handleShowWarningModal = useCallback(
    async (curStep, gotoFunc) => {
      setGoToNextStepFunc(() => gotoFunc);
      setActiveStep(curStep);
      if (curStep === 'AddingConsignment' || curStep === 'UpdatingConsignment') {
        await checkErrors('Consignments');
        setErrorsChecked(true);
      } else if (
        curStep !== 'ReviewAndSubmit' &&
        curStep !== 'Consignments' &&
        curStep !== 'uploadConsignment' &&
        isEditable
      ) {
        // only run error check if the shipment is editable (otherwise it's already accepted/cancelled)
        await checkErrors(curStep);
        setErrorsChecked(true);
      } else setErrorsChecked(true);
    },
    [checkErrors, errorsChecked]
  );

  //Check the errors object to see if there are any errors
  const allValid = useMemo(() => {
    const passedStandard =
      Object.keys(errors)
        .filter((k) => k !== 'Consignments')
        .map((k) => errors[k])
        .filter((v) => Object.keys(v).length > 0).length === 0;
    const passedConsignment =
      errors?.Consignments?.filter?.((c) => Object.keys(c).filter((k) => k !== 'lines' && k !== 'id').length > 0)
        .length === 0;
    const dataPresent = data.TransportAndJourney && data.Consignments && data.Consignments?.length > 0;
    return passedStandard && passedConsignment && dataPresent;
  }, [errors, data]);

  //set the isLocked state to be true if data.Consignments[n].statusId is not [30, 20, 50]
  // or if the header status is 110 (Error Sending to ThymeIT)
  const isLocked = useMemo(() => {
    if (!data?.Consignments) return false;
    return (
      data?.Consignments?.every((c) => [30, 20, 50].includes(c.statusId)) || data?.TransportAndJourney?.statusId === 110
    );
  }, [data]);

  //Does the current consignment have data? Used to determine if the consignment should be saved upon leaving the page.
  const currentConsignmentHasData = useMemo(() => {
    if (!currentConsignment) return false;
    if (currentConsignment?.lines?.length > 0) return true;
    const keys = Object.keys(currentConsignment);
    if (keys.filter((k) => k !== 'lines' && k !== 'Consignee' && k !== 'Consignor').length > 0) return true;
    if (Object.keys(currentConsignment.Consignee || {}).length > 0) return true;
    return Object.keys(currentConsignment.Consignor || {}).length > 0;
  }, [currentConsignment]);

  return {
    errors,
    setErrors,
    errorsHMRC,
    setErrorsHMRC,
    checkErrors,
    checkErrorsStandard,
    checkErrorsLocal,
    checkErrorsConsignment,
    checkErrorsConsignmentLocal,
    hasErrors,
    handleShowWarningModal,
    allValid,
    isLocked,
    isAccompanied,
    tabValidationErrors,
    HMRCtabValidationErrors,
    currentConsignmentHasData,
    errorsLocal,
    setErrorsLocal,
    initialErrorCheckRun,
  };
};

export default useErrors;
